import React, { useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import { ChevronDown, RefreshCcw, XCircle, Loader2, X } from 'lucide-react';
import { storeApi } from './storeApi';
import StoreLayout from './StoreLayout';

const STATUS_META = {
  created:                { label: 'Order Created',  cls: 'bg-slate-100 text-slate-700' },
  ready_for_pick:         { label: 'Ready for Pick', cls: 'bg-orange-100 text-orange-700' },
  stock_allocated:        { label: 'Stock Allocated', cls: 'bg-orange-100 text-orange-700' },
  pending_procurement:    { label: 'Pending Procurement', cls: 'bg-amber-100 text-amber-700' },
  picked:                 { label: 'Picked',         cls: 'bg-orange-200 text-orange-800' },
  invoiced:               { label: 'Invoiced',       cls: 'bg-blue-100 text-blue-800' },
  partially_invoiced:     { label: 'Partial Invoice', cls: 'bg-blue-100 text-blue-800' },
  dispatched:             { label: 'Dispatched',     cls: 'bg-indigo-100 text-indigo-800' },
  delivered:              { label: 'Delivered',      cls: 'bg-green-700 text-white' },
  cancelled:              { label: 'Cancelled',      cls: 'bg-red-100 text-red-700' },
};

const CANCELLABLE_STATUSES = new Set([
  'created', 'ready_for_pick', 'stock_allocated', 'pending_procurement',
  'picked', 'qa_completed',
]);

function chip(status) {
  const m = STATUS_META[status] || { label: (status || 'unknown').replace(/_/g, ' '), cls: 'bg-slate-100 text-slate-700' };
  return <span className={`inline-block px-2 py-0.5 rounded-full text-[10px] font-semibold uppercase ${m.cls}`}>{m.label}</span>;
}

export default function StoreOrders() {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState({});
  const [cancellingId, setCancellingId] = useState(null);
  const [cancellingLineUid, setCancellingLineUid] = useState(null);
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');

  function load() {
    setLoading(true);
    storeApi.get('/store/orders')
      .then((r) => setOrders(r.data.items || []))
      .catch(() => toast.error('Failed to load orders'))
      .finally(() => setLoading(false));
  }
  useEffect(load, []);

  // Filter client-side — order list is paginated to 2000 already, dates are
  // cheap to compare and avoids any new backend params.
  const filtered = useMemo(() => {
    if (!fromDate && !toDate) return orders;
    const fromTs = fromDate ? new Date(fromDate + 'T00:00:00').getTime() : null;
    // `toDate` is inclusive — match anything before end of that day.
    const toTs = toDate ? new Date(toDate + 'T23:59:59').getTime() : null;
    return orders.filter((o) => {
      const raw = o.created_at || o.uploaded_at;
      if (!raw) return true;
      const ts = new Date(raw).getTime();
      if (Number.isNaN(ts)) return true;
      if (fromTs && ts < fromTs) return false;
      if (toTs && ts > toTs) return false;
      return true;
    });
  }, [orders, fromDate, toDate]);

  function clearDateFilter() {
    setFromDate('');
    setToDate('');
  }

  async function cancelOrder(e, order) {
    e.stopPropagation();
    const reason = window.prompt(
      `Cancel order ${order.order_number}?\n\nThis releases any reserved stock and cannot be undone.\nOptionally tell us why:`,
      '',
    );
    if (reason === null) return; // user dismissed
    setCancellingId(order.id);
    try {
      const r = await storeApi.post(`/store/orders/${order.id}/cancel`, { reason });
      toast.success(`Order ${order.order_number} cancelled`);
      setOrders((prev) => prev.map((o) => (o.id === order.id ? r.data : o)));
    } catch (err) {
      const msg = err.response?.data?.detail || 'Cancellation failed';
      toast.error(typeof msg === 'string' ? msg : 'Cancellation failed');
    } finally {
      setCancellingId(null);
    }
  }

  async function cancelLine(orderId, line) {
    const reason = window.prompt(
      `Cancel line "${line.product_name}" (qty ${line.quantity})?\n\nReleases this line's reserved stock.\nReason (optional):`,
      '',
    );
    if (reason === null) return;
    setCancellingLineUid(line._uid);
    try {
      const r = await storeApi.post(`/store/orders/${orderId}/items/${line._uid}/cancel`, { reason });
      toast.success(`Line "${line.product_name}" cancelled`);
      setOrders((prev) => prev.map((o) => (o.id === r.data.id ? r.data : o)));
    } catch (err) {
      const msg = err.response?.data?.detail || 'Line cancellation failed';
      toast.error(typeof msg === 'string' ? msg : 'Line cancellation failed');
    } finally {
      setCancellingLineUid(null);
    }
  }

  return (
    <StoreLayout>
      <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
        <h1 className="store-headline text-3xl">My Orders</h1>
        <button onClick={load} className="text-sm text-orange-700 flex items-center gap-1 hover:underline"
                data-testid="store-orders-refresh">
          <RefreshCcw className="w-4 h-4" /> Refresh
        </button>
      </div>

      {/* Date range filter */}
      <div className="store-card p-3 mb-4 flex items-center gap-3 flex-wrap">
        <span className="text-xs font-semibold text-slate-500 uppercase">Filter by date</span>
        <div className="flex items-center gap-2">
          <label className="text-xs text-slate-600">From</label>
          <input type="date" value={fromDate} onChange={(e) => setFromDate(e.target.value)}
                 max={toDate || undefined}
                 className="store-input !py-1 !px-2 text-sm w-auto"
                 data-testid="orders-from-date" />
        </div>
        <div className="flex items-center gap-2">
          <label className="text-xs text-slate-600">To</label>
          <input type="date" value={toDate} onChange={(e) => setToDate(e.target.value)}
                 min={fromDate || undefined}
                 className="store-input !py-1 !px-2 text-sm w-auto"
                 data-testid="orders-to-date" />
        </div>
        {(fromDate || toDate) && (
          <button onClick={clearDateFilter}
                  className="text-xs text-orange-700 hover:underline flex items-center gap-1"
                  data-testid="orders-clear-date">
            <X className="w-3 h-3" /> Clear
          </button>
        )}
        <span className="ml-auto text-xs text-slate-400">
          Showing {filtered.length} of {orders.length} orders
        </span>
      </div>

      {loading ? (
        <div className="text-center py-20 text-orange-700">Loading…</div>
      ) : orders.length === 0 ? (
        <div className="store-card p-12 text-center text-slate-400">You haven&apos;t placed any orders yet.</div>
      ) : filtered.length === 0 ? (
        <div className="store-card p-12 text-center text-slate-400">
          No orders in the selected date range.
        </div>
      ) : (
        <div className="space-y-3" data-testid="store-orders-list">
          {filtered.map((o) => {
            const open = expanded[o.id];
            const canCancel = CANCELLABLE_STATUSES.has(o.status);
            return (
              <div key={o.id} className="store-card overflow-hidden" data-testid={`order-${o.id}`}>
                <div className="w-full p-4 flex items-center gap-3">
                  <button onClick={() => setExpanded((p) => ({ ...p, [o.id]: !p[o.id] }))}
                          className="flex-1 min-w-0 text-left">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-mono font-bold text-orange-900">{o.order_number}</span>
                      {chip(o.status)}
                      <span className="text-xs text-slate-400">{(o.created_at || '').split('T')[0]}</span>
                    </div>
                    <p className="text-xs text-slate-500 mt-0.5">
                      {o.items?.length || 0} item(s) · Segment: {o.business_segment || '—'}
                    </p>
                  </button>
                  {canCancel && (
                    <button
                      onClick={(e) => cancelOrder(e, o)}
                      disabled={cancellingId === o.id}
                      className="text-xs font-semibold text-red-600 hover:bg-red-50 px-3 py-1.5 rounded-md border border-red-200 disabled:opacity-50 inline-flex items-center gap-1"
                      data-testid={`cancel-order-${o.id}`}
                    >
                      {cancellingId === o.id
                        ? <Loader2 className="w-3 h-3 animate-spin" />
                        : <XCircle className="w-3 h-3" />}
                      Cancel
                    </button>
                  )}
                  <button onClick={() => setExpanded((p) => ({ ...p, [o.id]: !p[o.id] }))}
                          className="p-1">
                    <ChevronDown className={`w-5 h-5 text-orange-700 transition-transform ${open ? 'rotate-180' : ''}`} />
                  </button>
                </div>
                {open && (
                  <div className="border-t border-slate-200 p-4 bg-slate-50/40">
                    <table className="w-full text-sm">
                      <thead className="text-xs text-slate-500 uppercase">
                        <tr><th className="text-left py-1">Product</th>
                            <th className="text-right">Ordered</th>
                            <th className="text-right">Supplied</th>
                            <th className="text-right">Allocated</th>
                            <th className="text-right">Pending</th>
                            <th className="text-center">Line Status</th>
                            <th className="text-center w-12"></th></tr>
                      </thead>
                      <tbody>
                        {(o.items || []).map((it) => {
                          const lineCancellable =
                            o.status !== 'cancelled'
                            && it.status !== 'cancelled'
                            && it.status !== 'invoiced'
                            && !it.linked_invoice_id
                            && !it.linked_delivery_challan_id;
                          return (
                            <tr key={it._uid || it.product_id} className="border-t border-slate-200">
                              <td className="py-1">
                                {it.product_name}
                                {it.status === 'cancelled' && it.cancellation_reason && (
                                  <p className="text-[10px] text-red-600">Cancelled: {it.cancellation_reason}</p>
                                )}
                              </td>
                              <td className="text-right font-mono">{it.quantity}</td>
                              <td className="text-right font-mono" data-testid={`store-supplied-${it._uid || it.product_id}`}>
                                {it.supplied_qty != null ? it.supplied_qty : '—'}
                                {it.supplied_qty != null && parseFloat(it.supplied_qty) !== parseFloat(it.quantity) && (
                                  <span className={`block text-[10px] ${parseFloat(it.supplied_qty) < parseFloat(it.quantity) ? 'text-amber-600' : 'text-blue-600'}`}>
                                    {parseFloat(it.supplied_qty) < parseFloat(it.quantity) ? 'reduced' : 'increased'}
                                  </span>
                                )}
                              </td>
                              <td className="text-right font-mono">{it.allocated_qty}</td>
                              <td className="text-right font-mono">{it.pending_qty}</td>
                              <td className="text-center">{chip(it.status)}</td>
                              <td className="text-center">
                                {lineCancellable && (
                                  <button
                                    onClick={() => cancelLine(o.id, it)}
                                    disabled={cancellingLineUid === it._uid}
                                    className="text-red-600 hover:bg-red-50 p-1 rounded disabled:opacity-50"
                                    title="Cancel this line"
                                    data-testid={`cancel-line-${it._uid}`}
                                  >
                                    {cancellingLineUid === it._uid
                                      ? <Loader2 className="w-3.5 h-3.5 animate-spin" />
                                      : <XCircle className="w-3.5 h-3.5" />}
                                  </button>
                                )}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                    {o.notes && <p className="mt-3 text-xs text-slate-500"><b>Notes:</b> {o.notes}</p>}
                    {o.status === 'cancelled' && (o.cancellation_reason || o.cancelled_by_name) && (
                      <p className="mt-3 text-xs text-red-700">
                        <b>Cancelled</b>
                        {o.cancelled_by_name && <> by {o.cancelled_by_name}</>}
                        {o.cancelled_at && <> on {o.cancelled_at.split('T')[0]}</>}
                        {o.cancellation_reason && <> — {o.cancellation_reason}</>}
                      </p>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </StoreLayout>
  );
}
